import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";

/**
 * Stage 2 — "Confirm match" review queue (owner-only, Admin panel).
 * Each pending item pairs the INCOMING uploaded row (held, unwritten)
 * with the EXISTING candidate donor(s). Nothing touches a canonical donor
 * until the owner resolves the item: Match (merge) / Not a match (new
 * donor) / Skip for now (stays pending).
 */
const REASON_LABELS = {
  first_name_variant: "First-name variant",
  city_differs: "Different city",
  city_typo: "Likely city typo",
  city_blank: "City missing on one side",
  last_name_typo: "Possible last-name typo",
  multiple_exact: "Matches 2+ existing donors",
  moved_state_contact_match: "Same contact info, different state — possible move",
  multiple_contact_match: "Contact info matches 2+ donors",
};

const COMPARE_FIELDS = [
  ["first_name", "First name"],
  ["last_name", "Last name"],
  ["city", "City"],
  ["state", "State"],
  ["zip", "ZIP"],
  ["address", "Address"],
  ["phone", "Phone"],
  ["email", "Email"],
  ["employer", "Employer"],
  ["occupation", "Occupation"],
  ["last_gift_date", "Last gift date"],
  ["last_gift_amount", "Last gift $"],
  ["highest_gift", "Highest gift $"],
  ["total_given", "Total given $"],
  ["cycle_to_date", "Cycle-to-date $"],
];

const MONEY_FIELDS = new Set(["last_gift_amount", "highest_gift", "total_given", "cycle_to_date"]);

const fmtVal = (field, v) => {
  if (MONEY_FIELDS.has(field)) {
    const n = Number(v) || 0;
    return n > 0 ? n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }) : "—";
  }
  return String(v || "").trim() || "—";
};

const differs = (field, a, b) => {
  if (MONEY_FIELDS.has(field)) return (Number(a) || 0) !== (Number(b) || 0);
  return String(a || "").trim().toLowerCase() !== String(b || "").trim().toLowerCase();
};

export const ImportReviewQueue = ({ callerId, refreshToken = 0, onResolved = null }) => {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(true);
  const [busyId, setBusyId] = useState(null);
  // Session-only skips: item stays pending on the server, we just move on.
  const [skipped, setSkipped] = useState(new Set());

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/callers/${callerId}/import-review`);
      setItems(data.items || []);
    } catch {
      setItems([]);
    }
  };
  useEffect(() => { load(); setSkipped(new Set()); }, [callerId, refreshToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolve = async (item, action, donorId = null) => {
    setBusyId(item.id);
    try {
      const { data } = await api.post(
        `/admin/callers/${callerId}/import-review/${item.id}/resolve`,
        { action, donor_id: donorId }
      );
      toast.success(
        action === "match"
          ? `Merged into existing donor — ${data.pending_remaining} left to review`
          : `Added as a new donor — ${data.pending_remaining} left to review`
      );
      setItems((prev) => (prev || []).filter((x) => x.id !== item.id));
      onResolved && onResolved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const skip = (item) => {
    setSkipped((prev) => new Set(prev).add(item.id));
    toast.info("Skipped — it stays in the queue for later");
  };

  if (!items || items.length === 0) return null;
  const visible = items.filter((it) => !skipped.has(it.id));

  return (
    <div
      className="mt-4 border-2 border-amber-300 bg-amber-50 rounded-xl p-4"
      data-testid={`admin-review-queue-${callerId}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 text-left"
        data-testid={`admin-review-queue-toggle-${callerId}`}
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
          <div>
            <div className="font-display font-black text-amber-900" data-testid={`admin-review-queue-count-${callerId}`}>
              {items.length.toLocaleString()} possible match{items.length === 1 ? "" : "es"} need{items.length === 1 ? "s" : ""} review
            </div>
            <div className="text-[11px] text-amber-800">
              These uploaded rows are close to an existing donor but not an exact match. Nothing is written until you decide — Match merges into the existing donor, Not a match creates a new one.
            </div>
          </div>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-amber-700 shrink-0" /> : <ChevronDown className="h-4 w-4 text-amber-700 shrink-0" />}
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          {visible.length === 0 && (
            <div className="text-xs text-amber-800 italic" data-testid={`admin-review-queue-all-skipped-${callerId}`}>
              All remaining items skipped for now — they&rsquo;ll still be here next time.
            </div>
          )}
          {visible.map((it) => (
            <div
              key={it.id}
              className="bg-white border border-amber-200 rounded-lg p-3"
              data-testid={`admin-review-item-${it.id}`}
            >
              <div className="flex items-center gap-1.5 flex-wrap mb-2">
                <span className="text-xs font-bold text-flag-navy">
                  {it.incoming.first_name} {it.incoming.last_name}
                </span>
                {it.category_name && (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                    → {it.category_name}
                  </span>
                )}
                {it.reasons.map((r) => (
                  <span
                    key={r}
                    className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-900"
                    data-testid={`admin-review-reason-${it.id}-${r}`}
                  >
                    {REASON_LABELS[r] || r}
                  </span>
                ))}
              </div>

              {(it.candidates.length === 0 ? [null] : it.candidates).map((cand, ci) => (
                <div key={cand ? cand.id : "none"} className={ci > 0 ? "mt-3 pt-3 border-t border-slate-100" : ""}>
                  <div className="grid grid-cols-[auto_1fr_1fr] gap-x-3 text-[11px]">
                    <div />
                    <div className="font-bold uppercase tracking-wider text-slate-500 pb-1">
                      Existing donor{cand && cand.lists?.length > 0 ? ` (on: ${cand.lists.join(", ")})` : ""}
                    </div>
                    <div className="font-bold uppercase tracking-wider text-flag-navy pb-1">Incoming row (from file)</div>
                    {COMPARE_FIELDS.map(([f, label]) => {
                      const inc = it.incoming[f];
                      const exv = cand ? cand[f] : "";
                      // Hide rows where both sides are empty.
                      if (fmtVal(f, inc) === "—" && fmtVal(f, exv) === "—") return null;
                      const diff = cand ? differs(f, exv, inc) : false;
                      return (
                        <div key={f} className="contents">
                          <div className="text-slate-400 py-0.5 pr-1">{label}</div>
                          <div className={`py-0.5 px-1 rounded ${diff ? "bg-amber-100 font-semibold text-amber-950" : "text-slate-700"}`}>
                            {cand ? fmtVal(f, exv) : "—"}
                          </div>
                          <div className={`py-0.5 px-1 rounded ${diff ? "bg-amber-100 font-semibold text-amber-950" : "text-slate-700"}`}>
                            {fmtVal(f, inc)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {cand && (
                    <button
                      type="button"
                      onClick={() => resolve(it, "match", cand.id)}
                      disabled={busyId === it.id}
                      className="mt-2 text-[11px] font-bold px-3 py-1.5 rounded-md bg-flag-navy text-white hover:opacity-90 disabled:opacity-50"
                      data-testid={`admin-review-match-${it.id}-${cand.id}`}
                    >
                      Match — merge into {cand.first_name} {cand.last_name}
                    </button>
                  )}
                </div>
              ))}

              <div className="mt-2 pt-2 border-t border-slate-100 flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => resolve(it, "new")}
                  disabled={busyId === it.id}
                  className="text-[11px] font-bold px-3 py-1.5 rounded-md border-2 border-flag-navy text-flag-navy hover:bg-flag-navy hover:text-white disabled:opacity-50"
                  data-testid={`admin-review-new-${it.id}`}
                >
                  Not a match — add as new donor
                </button>
                <button
                  type="button"
                  onClick={() => skip(it)}
                  disabled={busyId === it.id}
                  className="text-[11px] font-semibold px-3 py-1.5 rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-50"
                  data-testid={`admin-review-skip-${it.id}`}
                >
                  Skip for now
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
